/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * RequestParameterEditor (#851) — the demand-source request-parameter editor,
 * rebuilt on the shared mapping paradigm (`docs/MAPPING_EDITOR_UX_GUIDANCE.md`).
 *
 * Replaces the bespoke resizable-column `ParameterEditor` table on the
 * demand-source surface: each parameter is a row whose value resolves through the
 * shared {@link MappingControl} (static / macro / path / expression), committed
 * with {@link RowEditActions}; the VAST catalog drives add-recommended; a sample
 * URL seeds rows; and issues surface through the shared {@link ValidationSummary}
 * (guidance §16). VAST-first — OpenRTB carries device/user/consent in the body,
 * so its catalog is empty and this surface is de-emphasised for ORTB (§14).
 *
 * `ParameterEditor` remains for the Req-Strategies override editor until that
 * surface is deep-designed (#852).
 */

import { getValueSuggestionsForSurface } from '../inputs/data-model-schema';
import type { FloorBound } from '../../../utils/floor-bound';
import { seedValueFor } from '@destination-field';
import React from 'react';
import { useExpressionNameLookup } from '../../../hooks/useExpressionLibrary';
import { Box, Button, HStack, Input, Text, Textarea, VStack } from '@chakra-ui/react';
import { MappingControl, type MappingSurfaceCapabilities } from './MappingControl';
import { MappingRow, MappingRowList, RowStateMark, type TemplateRevert } from './MappingRow';
import { AutomaticPanel } from './AutomaticPanel';
import { ParentRemovalRows, useStopOverriding, type ParentRemovals } from './parent-removals';
import { QUERY_PARAM_IS_TEXT } from './mapping-refusals';
import { rowValueResolves } from './mapping-help';
import { FieldHelpTooltip } from '../../ui/FieldHelpTooltip';
import { ValidationSummary } from './ValidationSummary';
import {
  describeRequiredMark,
  createParameterRow,
  rowToLeaf,
  applyLeafToRow,
  describeRowMapping,
  validateParams,
  applyParamFix,
  rowForParamName,
  revertDetectedRow,
  paramNameSuggestions,
  type ParameterRow,
  type ParamNameSuggestion,
} from './parameter-editor.adapters';
import { leafAddressesChangedExpression, leafDefault } from './mapping-value-round-trip';
import { getMissingParamFields } from './param-field-catalog';
import {
  AUTO_POPULATED_VAST_PARAMS,
  conceptsCoveredBy,
  coveringRowFor,
  displacedAutoParam,
  systemDefaultForParamRow,
} from '@auto-populated-params';
import { ProvenanceLegend } from './ProvenanceLegend';
import { rowsAfterApply, rowsAfterCancel, type RowEditSnapshot, type RowOrigin } from './row-edit-snapshot';
import {
  replaceValueInParams,
  rowOccurrenceMarks,
  surfaceConflictIssues,
  withSurface,
  type RequestOccurrences,
} from './request-occurrences';
import { useApplyToAll, type LocalValueChange } from './useApplyToAll';
import type { MappingIssue } from './mapping-issues';
import { rowSeverity } from './mapping-issues';
import { layerActionFor, originMarks, type RowOriginAnswer } from './row-origin-marks';
import { useDraftRevert } from './useDraftRevert';
import { resolutionForParam } from './resolved-request';
import type { RequestPreview } from '../../../hooks/useRequestPreview';
import type { MappingLeaf } from './ortb-body-editor.adapters';
import type { MacroExpression } from '../../../types/decorator-expression';
import type { DemandSourceMethod } from '../../../types';
import { detectedOriginFor, parseSampleUrl } from '../../../utils/mapping-suggest';
import { useRevealOnOpen } from './useRevealOnOpen';
import { rowAnchorId, useFindingReview, useRowOpenRequest, type RowOpenRequest } from './row-open-request';
import {
  draftRowInheritance,
  rowsWithStoredRow,
  paramRowPaths,
  rowChangedFromStored,
  rowLayerActionKind,
} from '../../../utils/artifact-inheritance';
import { InheritanceMark } from '../InheritanceMark';
import type { ArtifactInheritance } from '../../../types';
import { omissionOf, omissionToRow } from '@wire-omission';
import type { SavedLibraryEntry } from './DecoratorExpressionEditor';
import { suggestsSecretValue } from '@sensitive-headers';
import { useSystemDefaults } from '../../../hooks/useConfig';

export interface RequestParameterEditorProps {
  /**
   * **What the source's terms do to an authored floor** (`#918`) — `floorBoundOf` over the source
   * being edited. Required and nullable (`#1134`): a surface that does not know the terms passes
   * `null`, and a `bidfloor` below the source's minimum then goes unremarked there.
   */
  floorBound: FloorBound | null;
  rows: ParameterRow[];
  onChange: (rows: ParameterRow[]) => void;
  /**
   * Drives the catalog + validation. VAST has standard params; OpenRTB is empty.
   * Typed, and named for the DTO field it carries — see `paramCatalogFor` (#936).
   */
  method?: DemandSourceMethod;
  /** When embedded in a CollapsibleEditorSection the section owns the title. */
  embedded?: boolean;
  /** Read-only preview: description + rows only, no toolbar/validation/row actions. */
  readOnly?: boolean;
  /**
   * Which of this artifact's rows come from its parent (`P3a`).
   *
   * **Nullable rather than optional, for the reason `preview` is** — an optional prop threaded
   * through a shared component compiles, type-checks and renders nothing, which has already happened
   * four times in this codebase. `null` is a call site saying *this surface has no parent to report*;
   * omitting it entirely would be a call site that forgot.
   */
  inheritance: ArtifactInheritance | null;
  /**
   * **Where else this request sends each row's value** (`#944`) — the index the page builds over
   * parameters, headers and body together. Required and nullable: `null` is a surface with no request
   * to count against, and forgetting it would compile and render nothing.
   */
  requestOccurrences: RequestOccurrences | null;
  /**
   * The rows **as stored**, when this editor is showing a draft over them.
   *
   * `inheritance` is a fact about the stored artifact and does not move while a draft is open, so
   * without this a row the operator has just changed goes on rendering `inherited`. Omitted by the
   * wizard, where nothing is stored yet and every row is the operator's by construction.
   */
  baselineRows?: ParameterRow[];
  /**
   * **The template this source was copied from, for the way back to it** (Todd, 2026-09-24). A row
   * the operator changed from a template reads `overridden here`, and its revert puts back what the
   * template has for that row **now**. The source stays a copy (`TM9a`); this reads the template at
   * the moment of the revert and tracks nothing afterwards.
   *
   * **Required and nullable** (`#1134`): `null` where the source came from no template, the viewer
   * cannot see it, or the surface is read-only.
   */
  fromTemplate: { name: string; rows: readonly ParameterRow[] } | null;
  /**
   * Give these leaves back to the parent (`P3a`). Omit where reverting is not the gesture — a
   * read-only viewer, or a surface with no parent.
   *
   * **Offered in an open draft too** (Todd, 2026-09-18: *"the user needs a way to revert their
   * overrides to an inherited field"*). It is a server write that takes effect at once, so the
   * control says so, and the editor puts the stored row back into the draft when the source reloads
   * (`useDraftRevert`) — which is what used to make it unsafe: a save would have posted the override
   * straight back.
   */
  onRevert?: (paths: string[]) => void | Promise<unknown>;
  /**
   * **What this source removes from its parent, by parameter name** (`#1126`) — the parent's rows the
   * operator stopped overriding, so what the system sends automatically goes out instead.
   *
   * Required and nullable: `null` is a surface with no parent (the wizard). `stored` is what the
   * saved source removes, whose way back is revert; a name in `names` and not in `stored` was removed
   * in this draft, and its way back is Undo. `onChange` is `null` where nothing may change it (a
   * read-only preview), and carries the rows with the names because one gesture changes both.
   */
  parentRemovals: ParentRemovals<ParameterRow> | null;
  /** The parent's name, when the viewer may know it — used in the revert control's title. */
  parentName?: string;
  /** True while a revert is in flight. */
  isReverting?: boolean;
  /**
   * Where **this subtree** stands relative to the parent, rendered beside the heading.
   *
   * The section header above holds two subtrees — `headers` and `params` — and a chip each put
   * *inherited* twice in one header, which reads as a repeat rather than as two facts (Todd,
   * 2026-08-24). The header now says one thing about the section, and each subtree says its own
   * here, where the operator can see which one it is about.
   */
  sectionMark?: React.ReactNode;
  /**
   * **Save this expression to the shared library** (`#1134`).
   *
   * **Required, and nullable rather than optional** — the same treatment `preview` already has, for
   * the same reason. As `onSaveExpressionToLibrary?:` this compiled, type-checked, passed every spec
   * and **rendered nothing** on the two surfaces that forgot to pass it: `DemandSourceDetailPage`
   * and `SimplifiedDemandSourceWizard`, which is the surface an operator is most likely to build a
   * reusable expression on and the one they reach first.
   *
   * Fifth instance of that failure mode — the provenance work hit it four times in one increment
   * (`#898`) — so the fix is the prop's **type**, not the two call sites: a surface that does not
   * offer the control now has to say so, and one that forgot cannot compile.
   */
  onSaveExpressionToLibrary:
    | ((name: string, description: string, expr: MacroExpression) => Promise<SavedLibraryEntry>)
    | null;
  /**
   * Resolved-request preview (R4a/R4e). Each row shows what it will actually send.
   *
   * **Required, and nullable rather than optional** (2026-08-05) — see the note on
   * `HeadersEditorProps.preview`. `null` is the honest answer for a surface with no
   * source context (the library, a template editor): it renders no resolution rather
   * than a wrong one. What changed is that it must be *said*, because "no context"
   * and "nobody wired it" used to look identical.
   */
  preview: RequestPreview | null;
  /**
   * **The optional fields the applied template declares and withholds** (`#1166`).
   *
   * `applyTemplateToForm` deliberately does not seed an optional row with nothing in it — pre-adding
   * greeted every new source with a warnings panel about fields that did not matter. That is right,
   * and it left the other half undone: `iu`, `cust_params`, `pubid`, `inventory_route_id` are in no
   * catalog, in no *Sent automatically* panel and in no autocomplete, so the only way to one was
   * typing the vendor's spelling exactly. **Withholding without a reach is the defect**; this is the
   * reach.
   *
   * **Required and nullable rather than optional**, the treatment `preview`, `inheritance` and
   * `onSaveExpressionToLibrary` already carry for the reason `#1134` records: an optional prop
   * through a shared component compiles, type-checks and renders nothing. `null` is a surface saying
   * *no template is in scope here* — the detail page, until creation-from-template routes through
   * the clone stamp and `origin` records which template a saved source came from (`D31`/`D33`).
   */
  templateOptional: ParameterRow[] | null;
  /**
   * **Is the artifact being edited a template?** — which decides what an unfilled *required* row
   * means on the row itself (`describeRequiredMark`).
   *
   * On a template it is the shape (*You supply this*); on an instance it is unfinished. Same fact,
   * opposite readings, and the row could say neither before this.
   *
   * **Required and nullable**, the treatment `preview` and `inheritance` already carry: `null` is a
   * surface saying *I do not know which this is*, and a row then states only that it is required,
   * which is true either way.
   */
  isTemplate: boolean | null;
  /**
   * **The row a finding asked this editor to open or reveal** (`#1305`), or `null` where nothing on
   * the surface can point at a row — a read-only preview, or a caller with no findings panel.
   *
   * Required and nullable, the treatment every prop on a shared control carries since `#1134`: an
   * editor that silently ignored the request would look exactly like one nobody had pointed at.
   */
  openRow: RowOpenRequest | null;
}

export const RequestParameterEditor: React.FC<RequestParameterEditorProps> = ({
  rows,
  onChange,
  method,
  embedded = false,
  readOnly = false,
  fromTemplate,
  inheritance,
  requestOccurrences,
  baselineRows,
  onRevert,
  parentRemovals,
  parentName,
  isReverting,
  sectionMark,
  onSaveExpressionToLibrary,
  preview,
  templateOptional,
  isTemplate,
  openRow,
  floorBound,
}) => {
  // **The library is read once, above the rows** (`XS5`, `#1066`): a collapsed row's summary is
  // produced by a pure function, so the name of a shared expression is handed in rather than
  // fetched per row.
  const expressionNameFor = useExpressionNameLookup();
  const occurrences = React.useMemo(
    () => (requestOccurrences ? withSurface(requestOccurrences, 'params', { params: rows }) : null),
    [requestOccurrences, rows],
  );
  const applyToAll = useApplyToAll(occurrences);
  // Live-edit model (matches ResponseMappingEditor): the row updates as you edit;
  // Cancel restores the snapshot taken when the editor opened.
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [snapshot, setSnapshot] = React.useState<RowEditSnapshot<ParameterRow> | null>(null);
  // Whether a value marked secret is stored encrypted on this deployment (`CR7`); `undefined` says nothing.
  const encryptedAtRest = useSystemDefaults().data?.runtimeSecretsEncrypted;

  // Name-first add (#922 · 1 & 2), the same shape the ORTB body editor already uses:
  // an inline name input with a clickable catalog list beneath it. Reused rather than
  // re-invented — the two surfaces are one paradigm, and a parameter picker that looked
  // different from the leaf picker would be a second widget to keep in step.
  const [adding, setAdding] = React.useState(false);
  const [newName, setNewName] = React.useState('');

  /**
   * The auto-populated panel's disclosure (#951 · R5, Todd 2026-08-07: *"I like it being
   * collapsable — **open on first paste/parse or autopopulate**"*).
   *
   * **The docblock here described behaviour that was never built** (found 2026-08-27, Todd:
   * *"sent automatically still defaulting open in detail pages, and wizard after creation — I
   * thought we had them default close after 1st pass in editing"*). It claimed *"collapsing is
   * remembered for the session, and a paste re-opens it"* over a bare `useState(true)` with no
   * other writer than the header toggle: open on every mount, forever, and no re-open logic to
   * remember. A comment asserting a mechanism that does not exist is the #1045 defect turned
   * inward — it is why nobody went looking.
   *
   * So: **closed, and opened by the events that change what is in it.** *"These go out whether or
   * not you author them"* is news exactly once — when the set changes — and a panel that
   * re-announces itself on every visit is one an operator learns to collapse without reading.
   * A paste or a preset detection is what materially changes which fields are still automatic,
   * which is why those re-open it and a re-render does not.
   */
  /**
   * **A signal that re-opens the *Sent automatically* panel**, replacing a boolean the panel now
   * owns (`#1233`). The two places that bumped it are the two that change which fields are still
   * automatic — a paste, and applying a preset.
   */
  const [autoReopenKey, setAutoReopenKey] = React.useState(0);

  // Guidance §22 — adding a parameter appends to the end of a list that is routinely
  // taller than the viewport, so the editor it opens must come to the operator.
  const revealRef = useRevealOnOpen<HTMLDivElement>(editingId);

  const [pasteOpen, setPasteOpen] = React.useState(false);
  const [pasteText, setPasteText] = React.useState('');
  const [pasteError, setPasteError] = React.useState<string | null>(null);
  const [pasteNote, setPasteNote] = React.useState<string | null>(null);

  /**
   * Has this row diverged from what is stored? Keyed by **name**, because that is what the
   * inheritance paths are keyed by and what survives a re-projection; row ids do not.
   */
  const storedByName = React.useMemo(() => new Map((baselineRows ?? []).map((row) => [row.name, row])), [baselineRows]);
  const isRowChanged = React.useCallback(
    (row: ParameterRow) => rowChangedFromStored(storedByName.get(row.name), row),
    [storedByName],
  );

  /**
   * The row's inheritance chip **and its revert**, or its authoring provenance when inheritance has
   * nothing to say. One or the other, never both: two chips disagreeing about where one value came
   * from is what a single vocabulary exists to prevent.
   */
  /**
   * **Revert, in a draft or out of one** (`#1126`). Outside a draft it is the plain server write;
   * inside one, `useDraftRevert` also puts each reverted row back into the draft from the reloaded
   * source, so saving the draft cannot post the override straight back.
   */
  const revertFromParent = useDraftRevert({
    onRevert,
    baselineRows,
    rows,
    keyOf: (row: ParameterRow) => row.name,
    keysOf: (paths) => {
      // Asked of every name the reverted paths could belong to — a draft row, a stored one, or a
      // removal, which has no row at all.
      const names = new Set([
        ...rows.map((r) => r.name),
        ...(baselineRows ?? []).map((r) => r.name),
        ...(parentRemovals?.names ?? []),
      ]);
      return [...names].filter((name) =>
        paths.some((p) => paramRowPaths(name).some((base) => p === base || p.startsWith(`${base}.`))),
      );
    },
    apply: (nextRows, revertedNames) => {
      const removals = parentRemovals;
      if (removals?.onChange && removals.names.some((n) => revertedNames.has(n))) {
        removals.onChange({ names: removals.names.filter((n) => !revertedNames.has(n)), rows: nextRows });
      } else {
        onChange(nextRows);
      }
    },
  });

  /** *Stop overriding* and its Undo — shared with the headers editor (`parent-removals.tsx`). */
  const { stopOverriding, undo: undoStopOverriding } = useStopOverriding<ParameterRow>({
    parentRemovals,
    rows,
    nameOf: (row) => row.name,
    isSameRow: (a, b) => a.id === b.id,
    onRowLeaving: (row) => {
      if (editingId === row.id) {
        setEditingId(null);
        setSnapshot(null);
      }
    },
    commit: () => preview?.commit(),
  });

  /**
   * **Revert a draft-only change** — the stored row, which is the parent's, back into the draft
   * (`InheritanceMark.restoreDraft`). `undefined` where nothing is stored under this name.
   */
  const restoreStoredRow = React.useCallback(
    (row: ParameterRow) => {
      const stored = storedByName.get(row.name);
      if (!stored) return undefined;
      return () => {
        if (editingId === row.id) {
          setEditingId(null);
          setSnapshot(null);
        }
        onChange(rowsWithStoredRow(rows, row, stored));
        preview?.commit();
      };
    },
    [storedByName, editingId, onChange, rows, preview],
  );

  const rowInheritanceMark = React.useCallback(
    (row: ParameterRow): RowOriginAnswer => {
      const paths = paramRowPaths(row.name);
      const state = draftRowInheritance(inheritance, paths, isRowChanged(row));
      if (!state) return { origin: row.provenance };
      return {
        inheritance: (
          <InheritanceMark
            inheritance={inheritance}
            path={paths[0]}
            state={state}
            parentName={parentName}
            isReverting={isReverting}
            onRevert={revertFromParent ?? undefined}
            revertsNow={baselineRows !== undefined}
            restoreDraft={restoreStoredRow(row)}
          />
        ),
      };
    },
    [inheritance, isRowChanged, revertFromParent, baselineRows, parentName, isReverting, restoreStoredRow],
  );

  /** The request's conflicts this surface is in (`#944`) — their Fix opens the row, see the summary below. */
  const conflictIssues = React.useMemo(() => surfaceConflictIssues(occurrences, 'params'), [occurrences]);
  const issues = React.useMemo(
    () => [...validateParams(rows, method, floorBound, expressionNameFor), ...conflictIssues],
    [rows, method, floorBound, expressionNameFor, conflictIssues],
  );
  const existingNames = React.useMemo(
    () => new Set(rows.map((r) => r.name.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const missing = getMissingParamFields(method, existingNames);
  /** What the bulk add would add — the same set `validateParams` marks `fixable` (`#1254`). */
  const missingRequired = missing.filter((f) => f.importance === 'required');
  /**
   * What the ghosted panel lists — only what the driver actually sends unprompted, which
   * is a *subset* of the catalog. `us_privacy` is the difference and the reason this is
   * not just `missing`: it is a standard parameter worth offering and one we never
   * volunteer, so listing it as "sent automatically" would be a claim the wire denies.
   *
   * **And not what a row already covers under another name** — the driver dedupes by concept
   * (`conceptsCoveredBy`), so a `pod` row mapped to `{pod_max_duration}` sends instead of `dur`.
   * Filtering by name alone listed `dur` as sent while the wire carried only `pod`.
   */
  const autoPopulated = React.useMemo(() => {
    const covered = conceptsCoveredBy(rows.map((r) => ({ name: r.name, expression: r.expression })));
    return missing.filter((f) => {
      const auto = AUTO_POPULATED_VAST_PARAMS.find((p) => p.name === f.name);
      return auto !== undefined && coveringRowFor(auto, covered) === undefined;
    });
  }, [missing, rows]);

  /**
   * Re-announce the panel when its **contents** change, never when it merely re-renders.
   *
   * Keyed on the names rather than the count, because a paste can swap one automatic field for
   * another and leave the count alone — which is exactly the case worth showing. The first render
   * is skipped deliberately: arriving at a source is not an event that changed anything, and
   * treating it as one is the bug this replaced.
   */
  const autoPopulatedKey = autoPopulated.map((f) => f.name).join(',');
  const lastAutoKey = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (lastAutoKey.current !== null && lastAutoKey.current !== autoPopulatedKey && autoPopulatedKey !== '') {
      setAutoReopenKey((k) => k + 1);
    }
    lastAutoKey.current = autoPopulatedKey;
  }, [autoPopulatedKey]);

  /**
   * What the name box offers while it is open — catalog fields not already present,
   * narrowed by what has been typed. Capped at 8, matching the leaf picker.
   *
   * The list is the *decision*, so clicking an entry is the action (#922 · 1). A pill
   * an operator reads and then retypes by hand is a suggestion the product declined to
   * act on.
   */
  const nameSuggestions = React.useMemo(() => {
    if (!adding) return [];
    return paramNameSuggestions({
      catalog: missing,
      templateOptional: templateOptional ?? [],
      existingNames,
      query: newName,
    });
  }, [adding, newName, missing, templateOptional, existingNames]);

  /**
   * The template's optional fields not yet on this source — what `+ Add optional` adds.
   *
   * Safe in a way `+ Add recommended` was **not**, and the difference is the whole reason that
   * button stayed removed (`#951` · R5): every field it offered is sent automatically, so it
   * converted fifteen values the system maintains into fifteen rows the operator owns. These are
   * sent by **nobody** unless a row exists, so adding them changes what goes on the wire rather
   * than restating what already does.
   */
  const withheldOptional = React.useMemo(
    () => (templateOptional ?? []).filter((row) => !existingNames.has(row.name.trim().toLowerCase())),
    [templateOptional, existingNames],
  );

  // ── row operations ────────────────────────────────────────────────────────
  /**
   * **What this row falls back to, and whether the operator has declined it** (`X33a`, `#1251`, `#1427`).
   *
   * The answer is the driver's own (`systemDefaultForParamRow`), never a comparison made here: the
   * driver falls back on the parameter a row displaces **by concept** (`client_ip` for `ip`) and on
   * any name its resolver knows (`genre`). Asking only the first offered the control on seventeen
   * names while the driver fell back on the whole vocabulary, so a `genre` row fell back with no
   * control to see or decline it.
   *
   * `null` where the driver has nothing to fall back on: an unrecognised name, or a row with no
   * source of its own, where the name is the mapping and nothing can be declined.
   */
  const systemDefaultForRow = (row: ParameterRow): MappingSurfaceCapabilities['acceptsSystemDefault'] => {
    const systemDefault = systemDefaultForParamRow({ name: row.name, expression: row.expression });
    if (!systemDefault) return null;
    return {
      from: systemDefault.from,
      value: row.acceptSystemDefault !== false,
      onChange: (next) => patchRow(row.id, { acceptSystemDefault: next ? undefined : false }),
    };
  };

  const patchRow = (id: string, patch: Partial<ParameterRow>) => {
    // The model has moved, so the held resolution no longer describes it. Mark it
    // stale immediately rather than at the next Apply — the row must stop claiming a
    // value as soon as the value could have changed (R4e).
    preview?.invalidate();
    onChange(
      rows.map((r) => {
        if (r.id !== id) return r;
        // **Secret is suggested on the same way from blank to named, and only there** (`CR2`, `#1371`): a
        // row nobody has stated an answer on takes the name policy's suggestion, and the operator may untick it.
        const untouched = r.value.trim() === '' && !r.expression;
        const suggested =
          patch.name !== undefined && untouched && r.secret === undefined && suggestsSecretValue(patch.name)
            ? { secret: true }
            : {};
        return { ...r, ...patch, ...suggested };
      }),
    );
  };

  const setRowLeaf = (id: string, leaf: MappingLeaf) => {
    // The mapping is *the* thing the resolution mark describes, so changing it must
    // invalidate the mark — #999, which fixed this on the headers editor and left it
    // here. `patchRow` invalidated (name, optional, description) and this did not, so
    // the one edit that actually changes what the row sends was the one that kept a
    // stale resolution on screen: editing `pod_ads` from a static `3` to a macro left
    // the mark reading `3`, from a request the row no longer describes.
    preview?.invalidate();
    onChange(rows.map((r) => (r.id === id ? applyLeafToRow(r, leaf) : r)));
  };

  const removeRow = (id: string) => {
    if (editingId === id) {
      setEditingId(null);
      setSnapshot(null);
    }
    onChange(rows.filter((r) => r.id !== id));
    // A discrete change, so resolve it — `commit`, not `refresh`, because resolving
    // in this tick would resolve the model as it was before the removal.
    preview?.commit();
  };

  const openEditor = (row: ParameterRow, origin: RowOrigin) => {
    setSnapshot({ row, origin });
    setEditingId(row.id);
  };

  /**
   * **How this editor answers _which row is that_** — a parameter is named, and that name is what
   * `validateParams` puts in a finding's `ref`. Trimmed exact match first, then case-insensitively,
   * because a query parameter's name is the operator's own spelling and a finding quotes it back.
   */
  const resolveParamRow = (ref: string): ParameterRow | null => {
    const wanted = ref.trim();
    const lower = wanted.toLowerCase();
    return (
      rows.find((r) => r.name.trim() === wanted) ?? rows.find((r) => r.name.trim().toLowerCase() === lower) ?? null
    );
  };
  // `openEditor`, not `setEditingId` — the snapshot is what Cancel restores, and a row opened
  // without one would discard the operator's edit instead of reverting it (`#1262`).
  const openExistingRow = (row: ParameterRow) => openEditor(row, 'existing');
  const findingReview = useFindingReview(
    'params',
    resolveParamRow,
    (ref) => resolveParamRow(ref) !== null,
    openExistingRow,
  );
  useRowOpenRequest<ParameterRow>('params', openRow, resolveParamRow, openExistingRow);

  const applyEditor = () => {
    const openId = editingId;
    const held = snapshot;
    const current = rows.find((r) => r.id === openId);
    // **A value shared with other places asks before it changes alone** (`#944`) — `useApplyToAll`.
    // Its answer arrives as one commit, so this Apply and *Apply to all* are a single `onChange`.
    const commit = (change: LocalValueChange | null) => {
      setEditingId(null);
      setSnapshot(null);
      // Open and Apply is the operator accepting the row, so a detected guess becomes a decision
      // (`#1001`). The rule is `rowsAfterApply`'s, beside Cancel's.
      let next = rowsAfterApply(rows, (r) => r.id === openId, held);
      if (change) next = replaceValueInParams(next, change);
      if (next !== rows) onChange(next);
      if (change) occurrences?.applyElsewhere?.apply({ ...change, except: 'params' });
      // Apply is the commit boundary (§13) and therefore the resolve trigger (R4e).
      // Nothing else moves the model, so nothing else needs to ask.
      preview?.refresh();
    };
    if (!current) return commit(null);
    applyToAll.apply({
      before: held && held.origin === 'existing' ? rowToLeaf(held.row) : null,
      after: rowToLeaf(current),
      self: { surface: 'params', name: current.name.trim() },
      commit,
    });
  };

  // Restores the row, or removes it where the gesture that opened the editor created it (`#1262`).
  const cancelEditor = () => {
    const held = snapshot;
    setEditingId(null);
    setSnapshot(null);
    if (held) onChange(rowsAfterCancel(rows, held, (r) => r.id === held.row.id));
    // Cancel restores the row, so the held resolution is correct again — but only
    // if it predates the edit. Refreshing is the safe answer and costs one call.
    preview?.refresh();
  };

  /**
   * Add a parameter **by name, pre-mapped** (#922 · 1 & 2).
   *
   * The old `addBlankRow` appended a nameless static row and opened its editor, so the
   * operator typed a name into a row that had already decided it was a constant. Every
   * parameter whose name we recognise then arrived unmapped — at the one moment where
   * seeding it is cheapest and guidance §5 ("pre-map added fields, never leave a blank")
   * most obviously applies. `+ Add recommended` had honoured that rule all along; adding
   * one by hand did not, which made the rule look like a property of that button.
   *
   * Two sources of a default, in order, and both are **existing** answers rather than a
   * third classifier:
   *
   * 1. the parameter catalog's `defaultMacro`, via `rowFromCatalogField` — the same row
   *    `+ Add recommended` and the ValidationSummary's Fix produce, so all three paths
   *    add an identical row;
   * 2. otherwise `matchMacroName`, **the** name→macro matcher (#1003). Whole-token only:
   *    a substring rule is what mapped `position` to the OS string.
   *
   * A name we recognise neither way gets a static row, as before — that is a genuine
   * "we have no opinion", not a blank we could have filled.
   */
  const addNamed = (rawName: string) => {
    const name = rawName.trim();
    if (!name) return;
    if (existingNames.has(name.toLowerCase())) return;

    const next = rowForParamName(name, method);

    onChange([...rows, next]);
    preview?.commit();
    setAdding(false);
    setNewName('');
    openEditor(next, 'created');
  };

  /**
   * Add one entry from the picker.
   *
   * A template entry adds **its own row**, not a row rebuilt from its name: the template authored a
   * mapping, a `sendAs` and possibly a coverage claim, and `rowForParamName` knows none of that — it
   * would look up the name in a catalog the vendor's field is not in and produce a static blank.
   * Re-created rather than reused by reference so two adds of the same picker cannot share an `id`.
   */
  const addSuggestion = (entry: ParamNameSuggestion) => {
    if (!entry.row) return addNamed(entry.name);
    if (existingNames.has(entry.name.trim().toLowerCase())) return;
    const next = createParameterRow({ ...entry.row, id: undefined });
    onChange([...rows, next]);
    preview?.commit();
    setAdding(false);
    setNewName('');
    openEditor(next, 'created');
  };

  /**
   * `+ Add optional` — every field the template declares and withholds, at once (`#1166` · 1).
   *
   * No editor is opened afterwards, unlike a single add: the operator asked for the set, and
   * revealing one arbitrary member of it would claim that one is where to start.
   */
  const addAllOptional = () => {
    if (withheldOptional.length === 0) return;
    onChange([...rows, ...withheldOptional.map((row) => createParameterRow({ ...row, id: undefined }))]);
    preview?.commit();
    setAdding(false);
    setNewName('');
  };

  /**
   * `+ Add recommended` is gone (#951 · R5). Every field it offered is now **sent
   * automatically**, so bulk-adding them converted fifteen values the system maintains
   * into fifteen rows the operator owns and must keep correct — strictly worse, and the
   * #1013 shape: a button proposing what already happens. The "Sent automatically" panel
   * replaces it, and its per-row Edit is the precise version of the same action.
   */

  // The applier is shared with the readiness panel's staged fix (#899) — see
  // `applyParamFix`. Pressing Fix here and pressing it in the panel must add the
  // same row.
  const fixField = (name: string) => {
    onChange(applyParamFix(rows, name, method));
    preview?.commit();
  };

  const applyPaste = () => {
    setPasteError(null);
    const { suggestions, error } = parseSampleUrl(pasteText);
    if (error) {
      setPasteError(error);
      return;
    }
    const have = new Set(rows.map((r) => r.name.trim().toLowerCase()));
    const additions: ParameterRow[] = [];
    /** A value shaped like a viewer's, kept as written — said, never mapped (`#1016`). */
    const shapeNotes: string[] = [];
    for (const s of suggestions) {
      const name = s.key.split('.').pop() ?? s.key;
      if (have.has(name.toLowerCase())) continue;
      have.add(name.toLowerCase());
      if (s.perViewerShape) shapeNotes.push(s.reason);
      // Marked, with the detector's grade and what Revert returns to (`#1001`). This path marked
      // nothing, so every pasted row read as one the operator typed — the guesses included.
      additions.push(
        createParameterRow(
          s.kind === 'macro' && s.macroToken
            ? { name, mode: 'macro', value: s.macroToken, provenance: detectedOriginFor(s) }
            : {
                name,
                mode: 'static',
                value: s.sampleValue == null ? '' : String(s.sampleValue),
                provenance: detectedOriginFor(s),
              },
        ),
      );
    }
    onChange([...rows, ...additions]);
    // The reported case: a paste applies many rows at once and none of them were
    // in the last resolved request, so every one rendered a stale mark.
    preview?.commit();
    setAutoReopenKey((k) => k + 1);
    setPasteNote(
      [
        additions.length > 0
          ? `Mapped ${additions.length} parameter${additions.length === 1 ? '' : 's'} from the sample.`
          : 'No new parameters — everything in the sample is already configured.',
        ...shapeNotes,
      ].join(' '),
    );
    setPasteOpen(false);
    setPasteText('');
  };

  /** Put back what the sample carried in place of a detected mapping (`#1001`). */
  const revertRow = (id: string) => {
    onChange(rows.map((r) => (r.id === id ? revertDetectedRow(r) : r)));
    preview?.commit();
  };

  /** The way back to the template's current row, for a row changed from it — see `fromTemplate`. */
  const templateRevertFor = (row: ParameterRow): TemplateRevert | null => {
    if (readOnly || row.provenance?.kind !== 'overridden') return null;
    const fromIt = fromTemplate?.rows.find((r) => r.name === row.name);
    if (!fromTemplate || !fromIt) return null;
    return {
      templateName: fromTemplate.name,
      onRevert: () => {
        if (editingId === row.id) {
          setEditingId(null);
          setSnapshot(null);
        }
        onChange(rows.map((r) => (r.id === row.id ? { ...fromIt, id: row.id, provenance: { kind: 'template' } } : r)));
        preview?.commit();
      },
    };
  };

  return (
    <VStack
      gap={3}
      align="stretch"
      // **Keys belong to the editor, not to the dialog around it** (#1129).
      //
      // The wizard advances a step on Enter and closes on Escape, and its skip list is keyed on
      // *element type* — textarea, button, select, contenteditable — so a plain `<input>` that owns
      // those keys cannot say so. The add-a-parameter box did: Enter added the row **and** advanced
      // the step, which also swallowed the reveal (the row was added; the operator was moved off the
      // phase showing it), and Escape cancelled the add **and** closed the whole wizard.
      //
      // Guarding the container rather than each input is the fix the ORTB body editor already
      // shipped, and it is why the next control added in here needs no ceremony to be correct.
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== 'Escape') return;
        e.stopPropagation();
        // **Escape has to mean something here, or swallowing it is its own surprise** (#1129).
        //
        // Stopping it was correct — it must never reach the wizard and close it — but that left
        // Escape doing *nothing* inside an editor, which Todd reported as surprising: the key had
        // an effect before, and now it does not. So it closes the open row, which is what Cancel
        // does and what Escape means everywhere else. `cancelEditor` restores the pre-edit snapshot,
        // so this reverts rather than discards, and it is a no-op when no row is open.
        if (e.key === 'Escape' && editingId) cancelEditor();
      }}
    >
      <HStack justify={embedded ? 'flex-end' : 'space-between'}>
        {!embedded && (
          <HStack gap={1.5} align="center">
            <Text fontSize="sm" fontWeight="600" color="fg.default">
              Query Parameters
            </Text>
            {/* Was a footer under the rows (#1179) — true on every visit, so teaching. Rendered only
                where the footer was: embedded, the section owns the title and never showed either. */}
            <FieldHelpTooltip ariaLabel="About Query Parameters" content={rowValueResolves('parameter')} />
            {sectionMark}
          </HStack>
        )}
        {!readOnly && (
          <HStack gap={1.5}>
            {/* `#1166` · 1. Present only when the template actually declares fields this source does
                not have — a button offering nothing is the #1013 shape from the other side, and its
                count is what tells the operator whether pressing it is worth anything. */}
            {withheldOptional.length > 0 && (
              <Button
                size="xs"
                variant="outline"
                borderColor="border.default"
                onClick={addAllOptional}
                title={`Add the ${withheldOptional.length} optional field${
                  withheldOptional.length === 1 ? '' : 's'
                } this template declares. Nothing sends them unless you do.`}
              >
                + Add optional · {withheldOptional.length}
              </Button>
            )}
            <Button size="xs" variant="outline" borderColor="border.default" onClick={() => setPasteOpen((v) => !v)}>
              Paste sample…
            </Button>
            <Button
              size="xs"
              bg="accent.solid"
              color="fg.onAccent"
              _hover={{ bg: 'accent.ink' }}
              onClick={() => setAdding((v) => !v)}
            >
              + Add parameter
            </Button>
          </HStack>
        )}
      </HStack>

      {!readOnly && pasteOpen && (
        <Box borderWidth="1px" borderColor="border.default" borderRadius="8px" p={3} bg="bg.subtle">
          <Text fontSize="xs" color="fg.muted" mb={2}>
            Paste a VAST tag URL or a{' '}
            <Text as="span" fontFamily="mono">
              ?a=1&amp;b={'{ua}'}
            </Text>{' '}
            query string. Each parameter becomes a row, pre-mapped where the name matches a known macro.
          </Text>
          <Textarea
            size="sm"
            fontFamily="mono"
            fontSize="xs"
            rows={3}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="https://ads.example.com/vast?ip={ip}&ua={ua}&w=640&h=360"
          />
          {pasteError && (
            <Text fontSize="xs" color="error.solid" mt={1}>
              {pasteError}
            </Text>
          )}
          <HStack justify="flex-end" mt={2} gap={2}>
            <Button size="xs" variant="ghost" color="fg.muted" onClick={() => setPasteOpen(false)}>
              Cancel
            </Button>
            <Button size="xs" bg="accent.solid" color="fg.onAccent" _hover={{ bg: 'accent.ink' }} onClick={applyPaste}>
              Parse &amp; map
            </Button>
          </HStack>
        </Box>
      )}

      {/* Name-first add (#922 · 1 & 2). Deliberately the leaf picker's shape: a name
          input with a clickable list beneath, each entry adding the field pre-mapped. */}
      {!readOnly && adding && (
        <Box borderWidth="1px" borderColor="border.default" borderRadius="8px" p={3} bg="bg.subtle">
          <HStack gap={2}>
            <Input
              size="xs"
              bg="bg.card"
              fontFamily="mono"
              autoFocus
              value={newName}
              placeholder="parameter name — standard parameters autocomplete, or type your own"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addNamed(newName);
                if (e.key === 'Escape') {
                  setAdding(false);
                  setNewName('');
                }
              }}
            />
            <Button
              size="xs"
              bg="accent.solid"
              color="fg.onAccent"
              _hover={{ bg: 'accent.ink' }}
              onClick={() => addNamed(newName)}
              disabled={!newName.trim() || existingNames.has(newName.trim().toLowerCase())}
            >
              Add
            </Button>
            <Button
              size="xs"
              variant="ghost"
              color="fg.muted"
              onClick={() => {
                setAdding(false);
                setNewName('');
              }}
            >
              Cancel
            </Button>
          </HStack>
          {existingNames.has(newName.trim().toLowerCase()) && newName.trim() !== '' && (
            <Text fontSize="xs" color="warning.ink" mt={1.5}>
              {newName.trim()} is already configured — edit the existing row instead.
            </Text>
          )}
          {nameSuggestions.length > 0 && (
            <VStack
              align="stretch"
              gap={0}
              mt={2}
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="md"
              bg="bg.card"
              overflow="hidden"
            >
              {nameSuggestions.map((f) => (
                <HStack
                  key={f.name}
                  as="button"
                  px={3}
                  py={1.5}
                  gap={2}
                  textAlign="left"
                  _hover={{ bg: 'accent.subtle' }}
                  onClick={() => addSuggestion(f)}
                >
                  <Text fontFamily="mono" fontSize="xs" color="fg.default" flexShrink={0}>
                    {f.name}
                  </Text>
                  {/* The default is shown, not just applied — the operator is picking a
                      mapping as much as a name, and a value that appears without being
                      named reads as the editor having decided something private. */}
                  {/* Omitted rather than blank when the field is `operator`-supplied (`D32`):
                      an arrow pointing at nothing reads as a mapping that failed. */}
                  {f.seed && (
                    <Text fontFamily="mono" fontSize="xs" color="accent.solid" flexShrink={0}>
                      → {f.seed}
                    </Text>
                  )}
                  <Text fontSize="xs" color="fg.muted" truncate>
                    {f.description}
                  </Text>
                  <HStack gap={1.5} flexShrink={0} ml="auto">
                    {/* This vendor's own field, which no catalog of ours declares (`D34`) —
                        said plainly, because the operator's authority for it is the vendor's
                        integration guide rather than anything we can validate. */}
                    {f.row && (
                      <Text fontSize="10px" color="accent.solid">
                        from template
                      </Text>
                    )}
                    {/* Marked, not hidden (#1166 · 3). An entry that changes nothing must say so
                        where the operator is deciding, not three inches below in a panel. */}
                    {f.autoSent && (
                      <Text fontSize="10px" color="fg.subtle">
                        already sent
                      </Text>
                    )}
                    {f.importance === 'required' && (
                      <Text fontSize="10px" color="warning.ink">
                        required
                      </Text>
                    )}
                  </HStack>
                </HStack>
              ))}
            </VStack>
          )}
        </Box>
      )}

      {/* ── Auto-populated, and therefore visible (#951 · R5) ────────────────────
          The driver fills these from session context with no row authored, which fixed a
          silent loss — a source without an `ip` row used to send a request most exchanges
          decline to bid on, with the value in context the whole time. But population that
          nobody can see is the *other* half of the same defect, so it is shown here rather
          than only happening. Ghosted, because these are not the operator's rows: editing
          one promotes it to a real row that outranks the default. */}
      <AutomaticPanel
        title="Sent automatically"
        count={autoPopulated.length + (parentRemovals?.names.length ?? 0)}
        action="edit to override or suppress"
        reopenKey={autoReopenKey}
        caveat={
          /* The honest caveat (Todd, 2026-08-07). Over-sending is not free: some SSPs read an
             unrecognised query parameter as key-value targeting, so an extra is not always ignored —
             it can change what the exchange thinks is being asked for. We send the conventional core
             because a value we have and do not send is a silent loss, but only the operator's own
             integration doc settles which names *this* exchange reads. */
          <>
            These are the names most exchanges use. Check them against what your SSP&apos;s integration guide expects —
            a parameter it does not recognise may be read as targeting rather than ignored.
          </>
        }
      >
        {/* **The parent's rows this source stopped overriding** (`#1126`) — see `ParentRemovalRows`. */}
        <ParentRemovalRows
          removals={parentRemovals}
          parentName={parentName}
          readOnly={readOnly}
          insteadOf={() => 'the value sent automatically'}
          revert={revertFromParent ?? null}
          revertsNow={baselineRows !== undefined}
          isReverting={isReverting}
          pathsOf={paramRowPaths}
          undo={undoStopOverriding}
        />
        {autoPopulated.map((f) => (
          <HStack key={f.name} px={3} py={1.5} gap={3} _hover={{ bg: 'bg.canvas' }}>
            {/* Ghosted: `fg.subtle` throughout, so the eye reads the authored rows
                      above as the operator's and these as the system's. */}
            <Text fontFamily="mono" fontSize="sm" color="fg.subtle" minW="150px" maxW="240px" flexShrink={0} truncate>
              {f.name}
            </Text>
            <Box
              display="inline-flex"
              bg="bg.canvas"
              color="fg.subtle"
              borderRadius="full"
              px={2}
              py={0.5}
              flexShrink={0}
            >
              <Text fontSize="xs" fontFamily="mono">
                {seedValueFor(f)}
              </Text>
            </Box>
            <Text fontSize="xs" color="fg.subtle" truncate>
              {f.description}
            </Text>
            {!readOnly && (
              <Button
                size="xs"
                variant="ghost"
                color="accent.solid"
                ml="auto"
                flexShrink={0}
                onClick={() => addNamed(f.name)}
                aria-label={`Override ${f.name}`}
              >
                Edit
              </Button>
            )}
          </HStack>
        ))}
      </AutomaticPanel>

      {pasteNote && (
        <HStack justify="space-between" bg="info.tint" color="info.ink" px={3} py={1.5} borderRadius="6px">
          <Text fontSize="xs">{pasteNote}</Text>
          <Button size="xs" variant="ghost" color="info.ink" onClick={() => setPasteNote(null)}>
            Dismiss
          </Button>
        </HStack>
      )}

      {/**
       * **The bulk add, out of the findings banner and named for the act** (`#1254`) — see the
       * ORTB body editor for the reasoning. `fixable` is set only for a **required** catalog field
       * that is genuinely absent, so the gate and the act agree and the count is exact.
       */}
      {!readOnly && missingRequired.length > 0 && (
        <HStack px={0} py={2} gap={3} flexWrap="wrap">
          <Button
            size="xs"
            bg="accent.solid"
            color="fg.onAccent"
            _hover={{ bg: 'accent.ink' }}
            onClick={() =>
              // Fold, don't iterate: each `fixField` call emitted `[...rows, one]` from the same
              // closure, so a bulk add over three missing fields added the third and dropped the
              // other two.
              onChange(missingRequired.reduce((acc, f) => applyParamFix(acc, f.name, method), rows))
            }
          >
            Add {missingRequired.length} required parameter{missingRequired.length === 1 ? '' : 's'}
          </Button>
          <Text fontSize="xs" color="fg.muted">
            the ones this protocol's catalogue marks required
          </Text>
        </HStack>
      )}

      {!readOnly && (
        <ValidationSummary
          issues={issues}
          onFix={(issue) => {
            // A conflict's Fix opens the row to choose its value (`#944`); every other Fix adds the field.
            const conflicting = (conflictIssues as readonly MappingIssue[]).includes(issue);
            const row = conflicting ? rows.find((r) => r.name.trim() === issue.ref) : undefined;
            if (row) openEditor(row, 'existing');
            else if (issue.ref && !conflicting) fixField(issue.ref);
          }}
          review={findingReview}
        />
      )}

      {rows.length === 0 ? (
        <Box
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="border.default"
          borderRadius="8px"
          p={readOnly ? 3 : 4}
        >
          <Text fontSize="xs" color="fg.muted">
            {method === 'ORTB'
              ? 'OpenRTB sources carry device, user and consent in the request body — most need no query parameters.'
              : readOnly
                ? 'No query parameters.'
                : 'No query parameters authored. The standard context values are sent automatically — paste a sample URL or add a parameter to override one.'}
          </Text>
        </Box>
      ) : (
        <MappingRowList>
          {/*
            D18: the same sentence that guides an empty ORTB editor becomes a **warning** once
            rows exist, because now there is something to warn about. Most OpenRTB sources need
            no query parameters — the body is where an exchange reads device, user and consent —
            so rows here are worth a second look. Not a prohibition: an SSP that wants an
            account id or a routing token on the URL is a real integration, and deciding that
            for the operator is the thing this design rejects.
          */}
          {method === 'ORTB' && (
            <Box borderWidth="1px" borderColor="warning.border" bg="warning.tint" borderRadius="8px" px={3} py={2}>
              <Text fontSize="xs" color="warning.ink">
                OpenRTB sources carry device, user and consent in the request body — most need no query parameters.
                These {rows.length} will be appended to the endpoint URL as well as sent in the body where the concept
                has one.
              </Text>
            </Box>
          )}
          {/* One legend per surface (R4), listing only the chips actually present. */}
          <ProvenanceLegend values={rows.map((r) => r.provenance)} />
          {rows.map((row) => {
            const summary = describeRowMapping(row, expressionNameFor);
            const editing = editingId === row.id;
            return (
              <MappingRow
                key={row.id}
                rootRef={editing ? revealRef : undefined}
                rootId={rowAnchorId('params', row.name)}
                readOnly={readOnly}
                editing={editing}
                summaryLabel={summary.label}
                summaryKind={summary.kind}
                onEdit={() => openEditor(row, 'existing')}
                onApply={applyEditor}
                onCancel={cancelEditor}
                layerAction={layerActionFor(
                  rowLayerActionKind(
                    inheritance,
                    paramRowPaths(row.name),
                    // The driver's own dedupe, asked the same way (`#1251`): a parent's `player_width`
                    // stands in for the automatic `w`, so removing it here gives `w` back.
                    displacedAutoParam({ name: row.name, expression: row.expression }) !== undefined,
                  ),
                  `parameter ${row.name || 'row'}`,
                  {
                    remove: () => removeRow(row.id),
                    stopOverriding: stopOverriding ? () => stopOverriding(row) : null,
                  },
                )}
                editLabel={`Edit mapping for ${row.name || 'parameter'}`}
                marks={{
                  // **The row's own findings, at the row** (2026-09-22). The slot has existed since
                  // guidance §16 and no surface filled it, so the one mark that says something is
                  // wrong was rendered by nothing. The summary still states the sentence; this says
                  // which row it is about, which is what the operator is scanning for.
                  severity: rowSeverity(issues, row.name),
                  secret: row.secret === true,
                  // Inheritance wins where it has an opinion. `row.provenance` says how the value
                  // came to be authored (detected, from a template); inheritance says **whose** it
                  // is, which is the one that tells the operator where to go and edit it — and each
                  // carries its own way back (`originMarks`, `#1001`).
                  ...originMarks(
                    rowInheritanceMark(row),
                    readOnly ? null : () => revertRow(row.id),
                    templateRevertFor(row),
                  ),
                  // The default standing behind the mapping (#913). The mark has existed
                  // since R4 with nothing supplying it, because until #1128 no surface
                  // could author one — the display half of an offer with no writer.
                  default: leafDefault(rowToLeaf(row), expressionNameFor),
                  sharedChanged: leafAddressesChangedExpression(rowToLeaf(row), expressionNameFor),
                  ...rowOccurrenceMarks(occurrences, rowToLeaf(row), { surface: 'params', name: row.name }),
                  // Static rows already display their literal; `→ <same literal>`
                  // is noise, and noise is how a useful mark stops being read.
                  ...(preview && row.mode !== 'static'
                    ? {
                        resolution: resolutionForParam(row.name, preview),
                      }
                    : {}),
                  /* One slot, because `suppress` and `required` are near-opposites — *never
                     send* against *always send, even empty* — so a contradictory pair cannot
                     be rendered. `SUPPRESSED` shouts because it is a problem; the required
                     mark is quieter because it is a **declaration**, not a warning, and the
                     instance's *"has no value and won't be sent"* already lives in
                     `ValidationSummary` (pattern 16 — one warnings taxonomy). */
                  state: <RowStateMark suppressed={!!row.suppress} required={describeRequiredMark(row, isTemplate)} />,
                }}
                name={row.name}
                nameInput={
                  <Input
                    value={row.name}
                    onChange={(e) => patchRow(row.id, { name: e.target.value })}
                    placeholder="parameter name"
                    size="sm"
                    fontFamily="mono"
                    // See the note in `HeadersEditor` — a transparent name input reads
                    // as a label, not a field.
                    bg="bg.subtle"
                    borderColor="border.default"
                    _focus={{
                      borderColor: 'accent.solid',
                      boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)',
                    }}
                  />
                }
              >
                <MappingControl
                  leaf={rowToLeaf(row)}
                  onChange={(leaf) => setRowLeaf(row.id, leaf)}
                  onSaveToLibrary={onSaveExpressionToLibrary}
                  /* Marked for the surface: a path the driver's session does not carry says so (`#1524`). */
                  pathSuggestions={(q) => getValueSuggestionsForSurface(q, 'macroContext')}
                  pathPlaceholder={null}
                  /* **What the row declares, from its template** (`FD3a`, `1f-iii`). A query parameter
                     is text on the wire, so this grades and narrows the Static tab, and the refused
                     Type control shows it as declared; nothing converts to it. Our parameter catalog
                     declares importance rather than a type, so a row without one says nothing. */
                  declaredType={row.valueType ?? null}
                  staticSuggestions={null}
                  can={{
                    // An empty value goes out as `name=`, on VAST and ORTB alike (`forEachEffectiveParamField`).
                    emptyValue: 'sent-empty',
                    /* A query parameter carries whatever resolves, so every spelling of a value is
                       a legitimate source here — including a `{macro}` (`X27`). */
                    acceptsMacro: true,
                    acceptsDerived: true,
                    /* Every value on this surface reads the session, which is what a library expression
                       reads too (`X28a`). */
                    offersLibrary: true,
                    /* Parameter expressions resolve through `buildMacroContext` — the one every
                       outbound mapping path resolves against (`#1000`). */
                    resolvesAgainst: 'macroContext',
                    /* D23 · R3 (#1083). A query string is a `scalar` surface — the class, not
                       this surface by name, so the same control serves the header and pixel
                       editors without either re-deciding what it can carry. `object` is
                       therefore not offered here. */
                    surface: 'scalar',
                    /* A parameter row has nowhere to keep a scalar type
                       (`DemandSourceParamField` declares no `asType`, and `applyLeafToRow`
                       drops it), and a query string could not express one if it did. It used
                       to render anyway — a second control called **Send as**, three lines
                       above this one, that discarded every choice made in it. */
                    storesValueType: {
                      reason: QUERY_PARAM_IS_TEXT,
                    },
                    sendAs: {
                      value: row.sendAs,
                      onChange: (sendAs) => patchRow(row.id, { sendAs }),
                      // A query string carries text: nothing declares what a parameter sends.
                      sends: null,
                    },
                    /* **`X33a`** — this row displaces an auto-populated parameter whenever it covers
                       one of their concepts, and what it displaces is what it owes the operator when
                       it resolves to nothing (`#1252`). Asked through the **shared** matcher, because
                       a name comparison here would miss `client_ip` — the case the concept dedupe
                       exists for, and therefore the row the control matters most on. */
                    acceptsSystemDefault: systemDefaultForRow(row),
                    /* **`optional` already is the omit-versus-empty switch on this surface**,
                       so the Default hint defers to it rather than answering the same question
                       a second time (`N11f`, applied to omission rather than to the value). An
                       optional parameter with nothing to send is omitted; a required one sends
                       `foo=` — `vast.driver.ts` sets `resolvedValue = ''`. Saying *"leave blank
                       to omit"* beside a required row would be the #1045 defect inside the
                       control added to prevent one. */
                    whenUnresolved: row.optional ? 'omit' : 'sendEmpty',
                    /* `DemandSourceParamField.fallback` — its own slot since `N11f`, so a
                       macro row here can finally say what to send instead. */
                    storesMacroDefault: true,
                    /* The surface `D32` was originally written for (`TM4`, `#1170`). */
                    /* **Both, and inside the control** (`P1` slice 3). This pair used to render
                       here, as two checkboxes below the shared mapping editor — which is how an
                       editor stays shared on paper while each surface answers *is this on the
                       wire* for itself. A query string can express both: absent, and
                       present-with-no-value. */
                    /* **A separate claim from the pair below** (`X46`). This one says what the
                       destination wants; those say what the wire does when nothing resolves. They
                       were one boolean until 2026-09-02, so unchecking *Optional* to get `foo=`
                       also declared the field required, and choosing to omit it switched the Prove
                       phase's warning off. */
                    declaresImportance: {
                      value: row.importance,
                      onChange: (next) => patchRow(row.id, { importance: next }),
                    },
                    declaresSecret: {
                      value: row.secret === true,
                      onChange: (next) => patchRow(row.id, { secret: next }),
                      encryptedAtRest,
                    },
                    declaresOmission: {
                      value: omissionOf(row),
                      onChange: (next) => patchRow(row.id, omissionToRow(next)),
                      /* A demand source is the bottom layer on this axis — there is no source's
                         value beneath it to fall back to, so `X33`'s fourth answer does not arise
                         here. A parented source's *Revert to parent* is a different gesture
                         (`X31`): it gives the **stored** row back, rather than declining to state
                         one for this round. */
                      layering: null,
                    },
                  }}
                />
              </MappingRow>
            );
          })}
        </MappingRowList>
      )}
      {applyToAll.dialog}
    </VStack>
  );
};
